/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraph, getOwnerConfigFromEnv, toHygraphSlug, resolveHygraphTarget } from "@cosmos/hygraph-data";
import { Locale } from "@cosmos/hygraph-schema";
import { getSdk } from "@cosmos/hygraph-schema/dist/generated/resolvers.hygraph";
import { getSlugParam, getHygraphLocale, PageSlugProps } from "@cosmos/next-common";

export type CosmosTemplateKey =
	| "cosmosPageHeroAndSection"
	| "cosmosPageDirectory"
	| "cosmosPageBlogDetail"
	| "cosmosPageEulaKey"
	| "cosmosPageRedirect"
	| "cosmosPageLegalContent"
	| "cosmosPageSearch"
	| "cosmosPageResearch";

export const getKey = async (props: PageSlugProps, hygraphLocales: string[]): Promise<CosmosTemplateKey | null> => {
	const urlSlug = getSlugParam(props.params);

	const previewParam = props.searchParams.preview;
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	const target = await resolveHygraphTarget();
	const resolvedEndpoint = target?.endpoint;
	const resolvedOwnerId = target?.ownerId;

	const ownerConfig = await getOwnerConfigFromEnv(preview, resolvedOwnerId, resolvedEndpoint);
	const hygraphSlug = toHygraphSlug(urlSlug, ownerConfig.pageSlugPrefix);

	const sdk = getSdk(getHygraph(preview, resolvedEndpoint));
	const localeArray = [getHygraphLocale(hygraphLocales, props.params.lang), Locale.En] as Locale[];

	const res = await sdk.CosmosPageTemplates({
		slug: hygraphSlug,
		locale: localeArray,
	});

	const templateKeys: CosmosTemplateKey[] = Object.keys(res) as CosmosTemplateKey[];
	let firstValidCosmosTemplateKey: CosmosTemplateKey | null = null;

	for (let i = 0; i < templateKeys.length; i++) {
		const key = templateKeys[i];
		const value = res[key as keyof typeof res] as { __typename?: string; id?: string } | undefined | null;
		if (value?.__typename && value?.id) {
			firstValidCosmosTemplateKey = key;
			break;
		}
	}

	return firstValidCosmosTemplateKey;
};

const slugRegEx = /^\/[a-z0-9]+(?:[a-z0-9-+/]+)*$/;
const phpPathRegex = /\/[^/]*\.php\//;

const blockedPaths = new Set(["/static/", "/wp-content", "/_profiler", "/docs/awesomeads", "/debug/", "/dev/"]);
const blockedSubstrings = new Set([".", "%", "sssied", "fsssied"]);

/**
 * Validates a slug string for expected normal traits to try and block bots
 * @param str
 * @returns Boolean
 */
const checkSlugString = (str?: string): boolean => {
	if (!str) return true;
	if (str.length > 200) return false;

	for (const path of blockedPaths) {
		if (str.startsWith(path)) return false;
	}

	if (phpPathRegex.test(str)) return false;

	for (const substring of blockedSubstrings) {
		if (str.includes(substring)) return false;
	}

	return slugRegEx.test(str);
};

const localeRegEx = /[a-zA-Z]{2}(?:-[a-zA-Z]{4}|-[a-zA-Z]{2})?/;

/**
 * Validates a lang string for expected normal traits to try and block bots
 * @param str
 * @returns Boolean
 */
const checkLangString = (str?: string): boolean => {
	if (!str) return false;
	if (str.length > 200) return false;

	const hasInvalidChars = str.indexOf(".") !== -1 || str.indexOf("cosmos") !== -1;

	if (hasInvalidChars) {
		return false;
	}

	if (!localeRegEx.test(str)) {
		return false;
	}

	return true;
};

export const checkValidParams = (props: PageSlugProps): boolean => {
	const slug = getSlugParam(props.params);

	if (!checkSlugString(slug) || !checkLangString(props.params.lang)) {
		console.debug("checkValidParams() found invalid slug or invalid lang param");
		return false;
	}

	return true;
};
